import { createStore, del, get, getMany, keys, set, setMany } from 'idb-keyval'
import type { DocSnapshot, SavedSignature, SourceInfo } from '@/types'

/**
 * Local-only persistence (IndexedDB). Nothing ever leaves the device:
 *  - recent files with a thumbnail, so you can reopen them instantly
 *  - autosaved edits, so closing the tab never loses work
 *  - saved signatures / initials
 */
const meta = createStore('folio-recents', 'meta')
const blobs = createStore('folio-blobs', 'blobs')
const prefs = createStore('folio-prefs', 'prefs')

export interface RecentMeta {
  id: string
  name: string
  updatedAt: number
  pageCount: number
  size: number
  thumb?: string
  edited: boolean
}

export interface SessionRecord extends DocSnapshot {
  id: string
  name: string
  sources: SourceInfo[]
  formBytes?: Uint8Array
}

const MAX_RECENTS = 24

export async function listRecents(): Promise<RecentMeta[]> {
  const ids = (await keys(meta)) as string[]
  const all = (await getMany(ids, meta)) as (RecentMeta | undefined)[]
  return all.filter((m): m is RecentMeta => Boolean(m)).sort((a, b) => b.updatedAt - a.updatedAt)
}

/** Store the original bytes of every source of a session (only needed once per source). */
export async function storeSourceBytes(sessionId: string, srcId: string, bytes: Uint8Array) {
  const key = `${sessionId}:src:${srcId}`
  if ((await get(key, blobs)) === undefined) await set(key, bytes, blobs)
}

/** Overwrite a stored source (used to persist filled-in form fields). */
export async function replaceSourceBytes(sessionId: string, srcId: string, bytes: Uint8Array) {
  await set(`${sessionId}:src:${srcId}`, bytes, blobs)
}

export async function saveSession(rec: SessionRecord, m: Omit<RecentMeta, 'id' | 'name' | 'updatedAt'>) {
  await setMany(
    [
      [`${rec.id}:state`, { ...rec }],
      [`${rec.id}:time`, Date.now()],
    ],
    blobs,
  )
  const prev = (await get(rec.id, meta)) as RecentMeta | undefined
  await set(
    rec.id,
    { ...prev, ...m, thumb: m.thumb ?? prev?.thumb, id: rec.id, name: rec.name, updatedAt: Date.now() },
    meta,
  )
  await prune()
}

export async function loadSession(id: string) {
  const state = (await get(`${id}:state`, blobs)) as SessionRecord | undefined
  if (!state) return null
  const sourceBytes = new Map<string, Uint8Array>()
  for (const s of state.sources) {
    const b = (await get(`${id}:src:${s.id}`, blobs)) as Uint8Array | undefined
    if (b) sourceBytes.set(s.id, b)
  }
  const handle = (await get(`${id}:handle`, blobs)) as FileSystemFileHandle | undefined
  return { state, sourceBytes, handle }
}

export async function storeHandle(id: string, handle: FileSystemFileHandle | null) {
  if (handle) await set(`${id}:handle`, handle, blobs)
}

export async function deleteSession(id: string) {
  await del(id, meta)
  const ks = ((await keys(blobs)) as string[]).filter((k) => k.startsWith(`${id}:`))
  await Promise.all(ks.map((k) => del(k, blobs)))
}

async function prune() {
  const all = await listRecents()
  for (const m of all.slice(MAX_RECENTS)) await deleteSession(m.id)
}

export async function listSignatures(): Promise<SavedSignature[]> {
  return ((await get('signatures', prefs)) as SavedSignature[] | undefined) ?? []
}

export async function saveSignatures(list: SavedSignature[]) {
  await set('signatures', list, prefs)
}

export async function requestPersistence() {
  try {
    if (navigator.storage?.persisted && !(await navigator.storage.persisted()))
      await navigator.storage.persist()
  } catch {
    /* not supported */
  }
}
